const BASE_URL = process.env.REACT_APP_API_URL || '';

async function request(path, options = {}, { skipAuthRedirect = false } = {}) {
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });

  if (res.status === 401 && !skipAuthRedirect) {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new Error('Unauthorized');
  }

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const e = new Error((data && data.error) || `Request failed: ${res.status}`);
    e.status = res.status;
    throw e;
  }

  return data || {};
}

const api = {
  login: (email, password) =>
    request('/api/login', { method: 'POST', body: JSON.stringify({ email, password }) }),

  logout: () =>
    request('/api/logout', { method: 'POST' }),

  me: () =>
    request('/api/me', {}, { skipAuthRedirect: true }),

  getPortalCompanies: () =>
    request('/api/portal/companies'),

  getPortalAds: (companyId) =>
    request(`/api/portal/ads?company_id=${encodeURIComponent(companyId)}`),

  getPortalApplications: (companyId) =>
    request(`/api/portal/applications?company_id=${encodeURIComponent(companyId)}`),

  getPortalPlacementTypes: () =>
    request('/api/portal/placement-types'),

  createPortalAd: (data) =>
    request('/api/portal/ads', { method: 'POST', body: JSON.stringify(data) }),

  getPortalAd: (id) =>
    request(`/api/portal/ads/${id}`),

  updatePortalAd: (id, data) =>
    request(`/api/portal/ads/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  updatePortalAdStatus: (id, status) =>
    request(`/api/portal/ads/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),

  updatePortalAdTargeting: (id, data) =>
    request(`/api/portal/ads/${id}/targeting`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getPortalFeatures: () =>
    request('/api/portal/features'),

  getPortalTemplates: (adId) =>
    request(`/api/portal/ads/${adId}/templates`),

  createPortalCreative: (adId, data) =>
    request(`/api/portal/ads/${adId}/creative`, { method: 'POST', body: JSON.stringify(data) }),

  getPortalCreative: (adId) =>
    request(`/api/portal/ads/${adId}/creative`),

  activatePortalAd: (id) =>
    request(`/api/portal/ads/${id}/activate`, { method: 'POST' }),

  pausePortalAd: (id) =>
    request(`/api/portal/ads/${id}/pause`, { method: 'POST' }),

  resumePortalAd: (id) =>
    request(`/api/portal/ads/${id}/resume`, { method: 'POST' }),

  archivePortalAd: (id) =>
    request(`/api/portal/ads/${id}/archive`, { method: 'POST' }),

  unarchivePortalAd: (id) =>
    request(`/api/portal/ads/${id}/unarchive`, { method: 'POST' }),

  getAdReport: (adId, startTime, endTime, timeUnit) =>
    request(`/api/portal/ads/${encodeURIComponent(adId)}/report?startTime=${encodeURIComponent(startTime)}&endTime=${encodeURIComponent(endTime)}${timeUnit ? '&timeUnit=' + encodeURIComponent(timeUnit) : ''}`),

  getAdsSummary: (companyId) =>
    request(`/api/portal/ads-summary?company_id=${encodeURIComponent(companyId)}`),

  /**
   * Fetch ad-level metrics for a list of ad IDs.
   * AS-099: async metrics endpoint (replaces getAdsSummary in AdsList).
   *
   * @param {string[]} ids      - Ad IDs to query.
   * @param {string}   companyId
   * @param {string}   [from]   - ISO date range start (optional).
   * @param {string}   [to]     - ISO date range end (optional).
   * @returns {{ [adId: string]: { executed_impressions: number, clicks: number, click_rate: number } }}
   *
   * NOTE: The backend caps `ids` at 100 entries per request.
   * If AdsList ever needs to display more than 100 ads, the caller must chunk
   * `ids` into batches of ≤100 and merge the results before setting metricsMap.
   */
  getAdsMetrics: ({ ids, companyId, from, to } = {}) => {
    const params = new URLSearchParams();
    params.set('ids', Array.isArray(ids) ? ids.join(',') : String(ids));
    params.set('company_id', companyId);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return request(`/api/portal/ads/metrics?${params.toString()}`);
  },

  getApplicationPlacements: (appId) =>
    request(`/api/portal/applications/${appId}/placements`),

  getApplicationIntegrationType: (appUuid) =>
    request(`/api/portal/applications/${encodeURIComponent(appUuid)}/integration-type`),

  getPassbacks: (companyId) =>
    request(`/api/portal/passback?company_id=${encodeURIComponent(companyId)}`),

  createPassback: (data) =>
    request('/api/portal/passback', { method: 'POST', body: JSON.stringify(data) }),

  updatePassback: (id, data) =>
    request(`/api/portal/passback/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deletePassback: (id) =>
    request(`/api/portal/passback/${id}`, { method: 'DELETE' }),

  getMembers: () =>
    request('/api/portal/members'),

  createMember: (data) =>
    request('/api/portal/members', { method: 'POST', body: JSON.stringify(data) }),

  updateMemberStatus: (id, status) =>
    request(`/api/portal/members/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),

  getCreatives: (adId) =>
    request(`/api/portal/ads/${adId}/creatives`),

  getCreativeDetail: (adId, uuid) =>
    request(`/api/portal/ads/${adId}/creatives/${uuid}`),

  addCreative: (adId, data) =>
    request(`/api/portal/ads/${adId}/creatives`, { method: 'POST', body: JSON.stringify(data) }),

  deleteCreative: (adId, uuid) =>
    request(`/api/portal/ads/${adId}/creatives/${uuid}`, { method: 'DELETE' }),

  updateCreative: (adId, uuid, data) =>
    request(`/api/portal/ads/${adId}/creatives/${uuid}`, { method: 'PUT', body: JSON.stringify(data) }),

  renameCreative: (adId, uuid, name) =>
    request(`/api/portal/ads/${adId}/creatives/${uuid}/name`, { method: 'PATCH', body: JSON.stringify({ creative_name: name }) }),

  // AS-213: variant-only PATCH — avoids full-ad PUT that requires all required fields.
  updateAdVariant: (adId, variantKey, variantName) =>
    request(`/api/portal/ads/${adId}/variant`, { method: 'PATCH', body: JSON.stringify({ variant_key: variantKey, variant_name: variantName }) }),

  changeCreativeStatus: (adId, uuid, status) =>
    request(`/api/portal/ads/${adId}/creatives/${uuid}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),

  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${BASE_URL}/api/portal/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
      // No Content-Type header — browser sets multipart boundary automatically
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && data.error) || `Upload failed: ${res.status}`);
    return data || {};
  },
};

export default api;
